import { memo, useCallback, useMemo } from 'react';

import { TriangleDown } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { DragType } from '~/components/Shared/Drag/dragTypes';
import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import {
  WorkspaceSidebarBoardsList,
  type WorkspaceSidebarBoardsListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspaceSidebarBoardsListItemSkeleton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemSkeleton';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { WorkspaceSidebarSectionHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionHeader';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useFavoriteBoards } from '~/components/v2/hooks/useFavoriteBoards';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useRearrangeBoards } from '~/swr-hooks/gallery/useGalleryRearrange';

const SUPPORTED_BOARD_DRAG_TYPES = [DragType.boardNavWorkspace, DragType.boardNavLibrary, DragType.boardCard];

export const WorkspaceSidebarFavoriteBoards = memo(() => {
  const { scrollElement } = useScrollElementContext();
  const { data: boards, isLoading: isLoadingBoards } = useFavoriteBoards();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const { rearrangeFavoriteBoards } = useRearrangeBoards();

  const flatBoards = useMemo(() => {
    return boards?.pages.flatMap((page) => page.data) ?? [];
  }, [boards]);

  useFetchObjectsPermissions({
    objects: {
      boardIds: flatBoards.map((board) => board.id),
    },
  });

  const onBoardRearrange: WorkspaceSidebarBoardsListProps['onBoardRearrange'] = useCallback(
    ({ adjacentItem, boards, parentBoardId, board }) =>
      rearrangeFavoriteBoards({
        boards,
        draggedItem: board,
        adjacentItem,
        parentBoardId,
      }),
    [rearrangeFavoriteBoards],
  );

  const getCanRearrange: WorkspaceSidebarBoardsListProps['getCanRearrange'] = useCallback(() => true, []);

  /**
   * If there are no boards, hide this section copmletely.
   */
  if (flatBoards.length === 0 && !isLoadingBoards) {
    return null;
  }

  return (
    <WorkspaceSidebarSection className="contents" data-testid="WORKSPACE_SIDEBAR_FAVORITE_BOARDS">
      <WorkspaceSidebarSectionHeader>
        <Button
          appearance="ghost"
          className="text-grey-9 hover:bg-grey-5"
          color="grey"
          onClick={toggleCollapsed}
          size="small"
          suffix={<TriangleDown className={cn('size-4 transition-transform', collapsed && '-rotate-90')} />}
        >
          Favorite Boards
        </Button>
      </WorkspaceSidebarSectionHeader>
      {collapsed ? null : isLoadingBoards ? (
        <>
          <WorkspaceSidebarBoardsListItemSkeleton />
          <WorkspaceSidebarBoardsListItemSkeleton />
        </>
      ) : (
        <WorkspaceSidebarBoardsList
          boards={flatBoards}
          depth={1}
          dragType={DragType.boardNavFavorites}
          draggable={true}
          droppable={true}
          getCanRearrange={getCanRearrange}
          onBoardRearrange={onBoardRearrange}
          parentBoardId={null}
          scrollElement={scrollElement}
          supportedBoardDragTypes={SUPPORTED_BOARD_DRAG_TYPES}
        />
      )}
    </WorkspaceSidebarSection>
  );
});

WorkspaceSidebarFavoriteBoards.displayName = 'WorkspaceSidebarFavoriteBoards';
